import { defaultClassification, defaultScores, defaultOverride } from "../domain/types.js";
import { classifyHeuristic } from "./heuristicPatterns.js";
import { classifyWithClaude, isClaudeClassificationAvailable } from "./claudeClassifier.js";
import { fetchPlayStoreMetadata } from "./playStoreMetadata.js";
import { mergeSeverity, scanContentSeverity } from "./severityScanner.js";

/** @typedef {import('../domain/types.js').AppClassification} AppClassification */
/** @typedef {import('../domain/types.js').BehavioralCategory} BehavioralCategory */
/** @typedef {'clean'|'flagged'|'unknown'} ContentSeverity */
/** @typedef {'heuristic'|'play_store'|'ai'|'merged'} ClassificationSource */

/**
 * @typedef {Object} ClassifyAppInput
 * @property {string} displayName
 * @property {string} [packageName]
 * @property {string} [bundleId]
 * @property {boolean} [skipPlayStore]
 * @property {boolean} [skipAi]
 */

/**
 * @typedef {Object} ClassifyAppResult
 * @property {AppClassification} classification
 * @property {ClassificationSource} source
 * @property {string[]} matchedPatterns
 * @property {string[]} severityHits
 * @property {string} [playStoreRating]
 * @property {string} [aiReason]
 */

/**
 * Full pipeline: heuristic → Play Store severity scan → optional Claude refine.
 * @param {ClassifyAppInput} input
 * @returns {Promise<ClassifyAppResult>}
 */
export async function classifyApp(input) {
  const displayName = String(input.displayName || "").trim();
  const packageName = String(input.packageName || input.bundleId || "").trim();

  const h = classifyHeuristic(packageName, displayName);
  let category = h.primaryCategory;
  let contentSeverity = h.contentSeverity;
  let triggerTypes = [...h.triggerTypes];
  let confidence = h.confidence;
  let source = /** @type {ClassificationSource} */ ("heuristic");
  let matchedPatterns = [...h.matchedPatterns];
  let severityHits = [...h.severityHits];
  let playStoreRating;
  let aiReason;

  /** @type {import('./playStoreMetadata.js').PlayStoreMetadata|null} */
  let playMeta = null;
  if (packageName && !input.skipPlayStore) {
    try {
      playMeta = await fetchPlayStoreMetadata(packageName);
      if (playMeta) {
        source = "play_store";
        playStoreRating = playMeta.contentRating;
        const blob = `${playMeta.title} ${playMeta.description} ${playMeta.contentRating}`;
        const scan = scanContentSeverity(blob);
        severityHits = [...new Set([...severityHits, ...scan.hits])];
        contentSeverity = mergeSeverity(contentSeverity, scan.severity);

        if (playMeta.contentRating && /mature|18|adult/i.test(playMeta.contentRating)) {
          contentSeverity = mergeSeverity(contentSeverity, "flagged");
        }

        if (!triggerTypes.includes("sexualized_content_risk") && contentSeverity === "flagged") {
          triggerTypes = ["sexualized_content_risk", ...triggerTypes.filter((t) => t !== "none")];
        }
        confidence = Math.min(0.95, confidence + 0.1);
      }
    } catch {
      /* offline / CORS — keep heuristic */
    }
  }

  if (!input.skipAi && isClaudeClassificationAvailable() && packageName) {
    try {
      const ai = await classifyWithClaude({
        displayName,
        packageName,
        playTitle: playMeta?.title,
        playDescription: playMeta?.description,
        playContentRating: playMeta?.contentRating,
        heuristicCategory: category,
        heuristicSeverity: contentSeverity,
      });
      if (ai) {
        source = "ai";
        category = ai.primaryCategory;
        contentSeverity = mergeSeverity(contentSeverity, ai.contentSeverity);
        triggerTypes = ai.triggerTypes;
        confidence = Math.max(confidence, ai.confidence);
        aiReason = ai.reason;
      }
    } catch {
      /* API optional */
    }
  }

  const classification = buildClassification({
    primaryCategory: category,
    contentSeverity,
    triggerTypes,
    confidence,
    source,
    matchedPatterns,
    severityHits,
    playStoreRating,
    aiReason,
  });

  return {
    classification,
    source,
    matchedPatterns,
    severityHits,
    playStoreRating,
    aiReason,
  };
}

/**
 * Layer 1 only (sync) — for instant UI before deep pass completes.
 * @param {string} packageName
 * @param {string} displayName
 * @returns {ClassifyAppResult}
 */
export function classifyAppHeuristicOnly(packageName, displayName) {
  const h = classifyHeuristic(packageName, displayName);
  const classification = buildClassification({
    primaryCategory: h.primaryCategory,
    contentSeverity: h.contentSeverity,
    triggerTypes: h.triggerTypes,
    confidence: h.confidence,
    source: "heuristic",
    matchedPatterns: h.matchedPatterns,
    severityHits: h.severityHits,
  });
  return {
    classification,
    source: "heuristic",
    matchedPatterns: h.matchedPatterns,
    severityHits: h.severityHits,
  };
}

/**
 * @param {Object} p
 */
function buildClassification(p) {
  const distraction =
    p.primaryCategory === "shortform_feed" || p.primaryCategory === "manga_reading"
      ? 85
      : p.primaryCategory === "work_comms" || p.primaryCategory === "navigation"
        ? 25
        : 50;
  const risk = p.contentSeverity === "flagged" ? 90 : p.contentSeverity === "clean" ? 15 : 40;

  return {
    primaryCategory: p.primaryCategory,
    secondaryCategories: [],
    triggerTypes: p.triggerTypes,
    contentSeverity: p.contentSeverity,
    scores: {
      ...defaultScores(),
      distraction,
      risk,
      productivity: 100 - distraction,
      confidence: p.confidence,
    },
    override: defaultOverride(),
    classificationMeta: {
      source: p.source,
      classifiedAt: new Date().toISOString(),
      matchedPatterns: p.matchedPatterns || [],
      severityHits: p.severityHits || [],
      playStoreRating: p.playStoreRating,
      aiReason: p.aiReason,
    },
  };
}
